/* eslint-disable react/prop-types */
import { FC, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatDateOnly } from '../../../i18n/format';
import { ROUTES } from '../../../constants/data';
import { Typo } from '../../../components/Typo';
import PhotoFill from '../../../components/shared/PhotoFill';
import Button from '../../../components/shared/Button';
import MapPinIcon from '../../../components/icons/MapPinIcon';
import DistanceAway from '../../../components/shared/DistanceAway';
import DisLikeIcon from '../../../components/icons/DisLikeIcon';
import LikeIcon from '../../../components/icons/LikeButton';
import { AuthUserState } from '../../../context/AuthUserProvider';
import { useHandleSave } from '../../../hooks/useHandleSave';
import { showModal } from '../../../lib/redux/reducers/modal.slice';
import { setDirectionData } from '../../../lib/redux/reducers/direction.slice';
import { useGetStorefrontItems } from '../../../hooks/storfront/useGetStorefronts';
import { getListingPlaceholder } from '../../../util/listingPlaceholder';

// Shop popup card for the map (shop-first discovery). Leads with the shop (logo,
// name, rating), previews a few of its items, and links into the shop. The item
// thumbnails + count are fetched lazily here (only when a shop popup opens), so
// the main map query stays light.
type Props = {
  storefront: any;
  refetch?: () => void;
  // Sheet uniformity (Luke's F7 polish): in the mobile bottom sheet the card
  // goes full-width like SheetListingCard; desktop Leaflet popups keep the
  // fixed width (popups size to their content, so fluid would collapse).
  sheet?: boolean;
};

const ShopPopupCard: FC<Props> = ({ storefront, refetch, sheet = false }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authUser } = AuthUserState();
  const { save } = useHandleSave(refetch);
  const { listings, total } = useGetStorefrontItems(authUser?.id, Number(storefront?.id), 4, 0);

  const items = Array.isArray(listings) ? listings : [];
  const count = Number(total) || items.length;
  const rating = Number(storefront?.reviewAverage) || 0;
  const reviews = Number(storefront?.reviewCount) || 0;
  const avatar = storefront?.logo?.url || storefront?.image?.[0]?.url;
  const saved = !!(storefront?.saved_storefronts && storefront?.saved_storefronts.length > 0);

  // Seed the Map (Doge Spotters): a community-reported pin has no owner and no
  // items/reviews yet - show an "unclaimed" identity (who spotted it, when it
  // was verified) instead of shop stats. isCommunity is true only for the
  // 'unclaimed' claimStatus the approve flow stamps.
  const isCommunity = storefront?.claimStatus === 'unclaimed';
  // A claimed community shop whose new owner hasn't finished setup yet. The
  // editor can't save without an image, so "no image" == "still a stub" - show
  // the honest "setting up" state instead of a bare shop that looks finished.
  const hasImage = Array.isArray(storefront?.image) && storefront.image.length > 0;
  const isSettingUp = storefront?.claimStatus === 'claimed' && !hasImage;
  const spotter = storefront?.communityReporter?.username || storefront?.communityReporter?.name;
  const verifiedOn = storefront?.verifiedAt
    ? formatDateOnly(new Date(storefront.verifiedAt), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

  const goShop = () => navigate(`${ROUTES.STOREFRONT_DETAILS_ROUTE}/${storefront?.id}`);

  const onDirections = (e: any) => {
    e.stopPropagation();
    dispatch(showModal('direction'));
    dispatch(
      setDirectionData({
        current: { lat: authUser?.location?.latitude, long: authUser?.location?.longitude },
        destination: {
          lat: storefront?.location?.latitude,
          long: storefront?.location?.longitude
        }
      })
    );
  };

  const onSaveShop = (e: any) => {
    e.stopPropagation();
    save(
      storefront.id,
      'storefront',
      saved,
      saved ? Number(storefront?.saved_storefronts?.[0]?.id) : undefined
    );
  };

  return (
    <div className={`${sheet ? 'w-full' : 'w-[280px]'} rounded-xl bg-white font-inter`}>
      <div onClick={goShop} className="cursor-pointer p-3">
        <div className="flex items-start gap-2">
          {avatar ? (
            <img
              src={avatar}
              alt={storefront?.name}
              className="h-10 w-10 flex-shrink-0 rounded-full border border-gray-200 object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 text-base font-semibold text-yellow-700">
              {(storefront?.name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <Typo.textSm fontWeight="font-medium" className="truncate text-gray-900">
              {storefront?.name}
            </Typo.textSm>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
              {isCommunity ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F7F0] px-1.5 py-0.5 font-medium text-[#0E9384] dark:bg-[#172B23] dark:text-[#5FE9D0]">
                  <FormattedMessage
                    id="page.marketplace.community_spot"
                    defaultMessage="📍 Community spot"
                  />
                </span>
              ) : isSettingUp ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-1.5 py-0.5 font-medium text-[#92400E] dark:bg-[#2B2717] dark:text-[#FCD34D]">
                  <FormattedMessage
                    id="page.marketplace.setting_up"
                    defaultMessage="🛠 New owner — setting up"
                  />
                </span>
              ) : reviews > 0 ? (
                <span className="text-yellow-600">
                  ★ {rating.toFixed(1)} <span className="text-gray-400">({reviews})</span>
                </span>
              ) : (
                <span>
                  <FormattedMessage id="page.marketplace.shop_label" defaultMessage="Shop" />
                </span>
              )}
              {count > 0 ? (
                <span>
                  <FormattedMessage
                    id="page.marketplace.item_count"
                    defaultMessage="· {count} items"
                    values={{ count }}
                  />
                </span>
              ) : null}
              <DistanceAway
                latitude={storefront?.location?.latitude}
                longitude={storefront?.location?.longitude}
                withDot={true}
              />
            </div>
            {isCommunity ? (
              <div className="mt-1 text-xs text-gray-500">
                {spotter ? (
                  <span>
                    <FormattedMessage
                      id="page.marketplace.spotted_by"
                      defaultMessage="Spotted by @{spotter}"
                      values={{ spotter }}
                    />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage id="page.marketplace.unclaimed" defaultMessage="Unclaimed" />
                  </span>
                )}
                {verifiedOn ? (
                  <span>
                    {' '}
                    <FormattedMessage
                      id="page.marketplace.verified_on"
                      defaultMessage="· Verified {date}"
                      values={{ date: verifiedOn }}
                    />
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onSaveShop}
            // In the mobile bottom sheet the sheet's own close X sits absolute
            // in the top-right corner - exactly where this heart lands. me-14
            // slides the heart left so the two 44px targets sit side by side
            // instead of on top of each other.
            className={`-m-2 flex h-11 w-11 items-center justify-center rounded-full text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 ${
              sheet ? 'me-14' : ''
            }`}
            aria-label={intl.formatMessage({
              id: 'page.marketplace.save_shop',
              defaultMessage: 'Save shop'
            })}>
            {saved ? (
              <DisLikeIcon className="text-gray-700" />
            ) : (
              <LikeIcon className="text-gray-500" />
            )}
          </button>
        </div>

        {items.length > 0 ? (
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            {items.slice(0, 4).map((it: any, i: number) => (
              <div
                key={it?.id ?? i}
                className="relative aspect-square overflow-hidden rounded-md bg-gray-50">
                <PhotoFill
                  className="h-full w-full"
                  src={it?.image?.[0]?.url || getListingPlaceholder('storefrontItem')}></PhotoFill>
                {i === 3 && count > 4 ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-medium text-white">
                    +{count - 4}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex gap-2 px-3 pb-3">
        <Button theme="primary-yellow" size="sm" className="flex-1" onClick={goShop}>
          <Typo.textSm fontWeight="font-semibold" className="text-gray-900">
            {isCommunity ? (
              <FormattedMessage id="page.marketplace.view_details" defaultMessage="View details" />
            ) : (
              <FormattedMessage id="page.listing.view_shop" defaultMessage="View shop" />
            )}
          </Typo.textSm>
        </Button>
        <Button
          theme="base"
          size="sm"
          onClick={onDirections}
          icon={<MapPinIcon className="stroke-blue-gray-600" />}></Button>
      </div>
    </div>
  );
};

// Memoized (map plan A4 convention) - popup content shouldn't re-render on
// every MapboxLe render during pans.
export default memo(ShopPopupCard);
